// ModalView.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as Portals from 'react-reverse-portal';

import ModalTracking from '../component/ModalTracking.jsx';

import ScrollLock from './ScrollLock.jsx';

const MODAL_ROOT_ID = '#modal-root';
const isServer = typeof window === 'undefined';
let globalIsFirstFrame = true;
let createPortal = () => {};

class ModalView extends React.PureComponent {
  state = { isFirstFrame: globalIsFirstFrame };
  nextTick = null;
  portalNode = null;

  componentDidMount() {
    const { isFirstFrame } = this.state;
    if (isFirstFrame) {
      this.nextTick = setTimeout(async () => {
        const { createPortal: loadedCreatePortal } = await import(
          '../partial/react-dom/createPortal.js'
        );
        createPortal = loadedCreatePortal;
        globalIsFirstFrame = false;
        return this.setState({ isFirstFrame: false });
      });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.nextTick);
  }

  render() {
    const { isFirstFrame } = this.state;
    const {
      id,
      zIndex,
      status,
      rootId,
      modalId,
      noScrollLock,
      children,
      modalViewStyle,
      render,
    } = this.props;
    if (!zIndex) {
      return null;
    }

    if ((!children && !render) || (children && render)) {
      throw new Error('Should provide either `chilren` or `render` function');
    }

    const clonedElement = (
      <ModalTracking modalId={modalId} id={id}>
        {React.cloneElement(render?.() || children, {
          status,
        })}
      </ModalTracking>
    );

    const view = (
      <StyledModalView
        zIndex={zIndex}
        isUnderModalRoot={MODAL_ROOT_ID === rootId}
        style={modalViewStyle}
      >
        {noScrollLock ? (
          clonedElement
        ) : (
          <ScrollLock>{clonedElement}</ScrollLock>
        )}
      </StyledModalView>
    );

    /**
     * Render in-place on server side and client side first frame.
     * Use react-reverse-portal to move in-place DOMs to react portal.
     */

    if (isServer) {
      return view;
    }

    if (!this.portalNode) {
      this.portalNode = Portals.createHtmlPortalNode();
    }

    const inPortal = (
      <Portals.InPortal node={this.portalNode}>{view}</Portals.InPortal>
    );
    const outPortal = <Portals.OutPortal node={this.portalNode} />;

    const domNode = document.querySelector(rootId);

    return (
      <>
        {inPortal}
        {isFirstFrame
          ? outPortal
          : domNode
            ? createPortal(outPortal, domNode)
            : null}
      </>
    );
  }
}

ModalView.propTypes = {
  zIndex: PropTypes.number,
  status: PropTypes.string,
  rootId: PropTypes.string,
  id: PropTypes.string,
  modalId: PropTypes.string,
  noScrollLock: PropTypes.bool,
  children: PropTypes.object,
  modalViewStyle: PropTypes.object,
  render: PropTypes.func,
};

ModalView.defaultProps = {
  zIndex: null,
  status: 'display',
  modalId: '',
  id: '',
  rootId: MODAL_ROOT_ID,
  noScrollLock: false,
  modalViewStyle: {},
};

const StyledModalView = styled.div.attrs(({ zIndex, isUnderModalRoot }) => ({
  style: { zIndex: isUnderModalRoot ? zIndex : 'auto' },
}))`
  position: fixed;
  top: 0px;
  left: 0px;
`;

export default ModalView;
